import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { CheckPinMembershipQuery } from 'generated/graphql';
import type { PinItemType } from '~/modules/moodboards/type';

type PinMembershipType =
    | 'page'
    | 'image'
    | 'download'
    | 'tearSheet'
    | 'lookbook';
type PinMembershipCache = Map<string, Set<number>>;

/** Flat set used by tiles for fast "is saved" checks. */
export const savedPinKeysAtom = atom<Set<string>>(new Set<string>());
/** Per-pin board membership cache used by FollowSelector and quick unsave. */
export const pinMembershipCacheAtom = atom<PinMembershipCache>(new Map());
/** Last board used by quick-save. */
export const lastUsedBoardIdAtom = atomWithStorage<number | null>(
    'lastUsedBoardId',
    null,
);

/**
 * Converts PinItemType to API membership type.
 *
 * Each kind is asked about on its own terms: a download's id is a file's, a
 * generated document's is the page it is rendered from, and a page's is the
 * page's. Answering any of them as a page would compare an id to the wrong
 * column and read some unrelated saved page as this row.
 */
const PIN_MEMBERSHIP_TYPES: Record<PinItemType, PinMembershipType> = {
    ProjectPin: 'page',
    ProductPin: 'page',
    ProfessionalPin: 'page',
    ArticlePin: 'page',
    PhotoPin: 'image',
    MemberUploadPin: 'page',
    DownloadPin: 'download',
    TearSheetPin: 'tearSheet',
    LookbookPin: 'lookbook',
};

const getPinMembershipType = (
    pinType?: PinItemType | PinMembershipType | string,
): PinMembershipType => {
    if (pinType && pinType in PIN_MEMBERSHIP_TYPES) {
        return PIN_MEMBERSHIP_TYPES[pinType as PinItemType];
    }

    return pinType === 'image' ? 'image' : 'page';
};

/** Stable key for saved-state lookups. */
export const makePinKey = (
    pinId: number,
    pinType?: PinItemType | PinMembershipType | string,
): string => `${getPinMembershipType(pinType)}:${pinId}`;

/** Resolves the tracked pin ID used by save-state atoms and membership cache. */
export const resolveTrackedPinId = (
    itemId: number,
    libraryItemId: number | undefined,
    itemType: PinItemType,
): number => (itemType === 'PhotoPin' ? itemId : (libraryItemId ?? itemId));

/** Deep-clones membership cache so optimistic rollback is safe. */
export const cloneMembershipCache = (
    cache: PinMembershipCache,
): PinMembershipCache => {
    const next: PinMembershipCache = new Map();
    cache.forEach((boardIds, key) => {
        next.set(key, new Set(boardIds));
    });
    return next;
};

/** Adds keys to the saved pins set. */
export const addSavedPinKeys = (
    prev: Set<string>,
    pinKeys: string[],
): Set<string> => {
    const next = new Set(prev);
    pinKeys.forEach((pinKey) => {
        next.add(pinKey);
    });
    return next;
};

/** Removes keys from the saved pins set. */
export const removeSavedPinKeys = (
    prev: Set<string>,
    pinKeys: string[],
): Set<string> => {
    const next = new Set(prev);
    pinKeys.forEach((pinKey) => {
        next.delete(pinKey);
    });
    return next;
};

/** Adds board membership for pin keys. */
export const addPinMembership = (
    prev: PinMembershipCache,
    pinKeys: string[],
    boardId: number,
): PinMembershipCache => {
    const next = cloneMembershipCache(prev);
    pinKeys.forEach((pinKey) => {
        const boardIds = next.get(pinKey) ?? new Set<number>();
        boardIds.add(boardId);
        next.set(pinKey, boardIds);
    });
    return next;
};

/** Removes board membership and returns keys that are no longer saved anywhere. */
export const removePinMembership = (
    prev: PinMembershipCache,
    pinKeys: string[],
    boardId: number,
): { cache: PinMembershipCache; keysRemovedFromAllBoards: string[] } => {
    const next = cloneMembershipCache(prev);
    const keysRemovedFromAllBoards: string[] = [];

    pinKeys.forEach((pinKey) => {
        const boardIds = next.get(pinKey);
        if (!boardIds) {
            return;
        }

        boardIds.delete(boardId);
        if (boardIds.size === 0) {
            next.delete(pinKey);
            keysRemovedFromAllBoards.push(pinKey);
            return;
        }

        next.set(pinKey, boardIds);
    });

    return { cache: next, keysRemovedFromAllBoards };
};

/** Returns saved board IDs for a pin from the membership cache. */
export const getSavedBoardIdsForPin = (
    cache: PinMembershipCache,
    pinId: number,
    pinType?: PinItemType | PinMembershipType | string,
): number[] => {
    const key = makePinKey(pinId, pinType);
    return [...(cache.get(key) ?? new Set<number>())];
};

/** Whether membership cache has data for this pin key. */
export const hasMembershipCacheForPin = (
    cache: PinMembershipCache,
    pinId: number,
    pinType?: PinItemType | PinMembershipType | string,
): boolean => {
    const key = makePinKey(pinId, pinType);
    return cache.has(key);
};

/** Fetches board membership for a pin from Remix API. */
export const fetchPinMembershipBoardIds = async (
    pinId: number,
    pinType?: PinItemType | PinMembershipType | string,
): Promise<number[]> => {
    const membershipType = getPinMembershipType(pinType);
    const timestamp = Date.now();
    const response = await fetch(
        `/remix-api/pin-membership?pinId=${pinId}&pinType=${membershipType}&_t=${timestamp}`,
        {
            method: 'get',
            credentials: 'same-origin',
        },
    );

    if (!response.ok) {
        throw new Error('Failed to fetch pin membership');
    }

    const data = (await response.json()) as CheckPinMembershipQuery;
    return data.checkPinMembership?.boardIds?.map(Number) ?? [];
};
